import React, { useEffect, useRef, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { AuthMode, useCognitoAuth } from '../../../auth/useCognitoAuth';
import { getSignedUser } from '../../../lib/auth';
import axios from '../../../lib/axios.factory';
import {
  ensureUserReady,
  HUB_REDIRECT_LOADING_TEXT,
  isRedirectingResult,
  ReadinessResult,
  V2_ONBOARD_PATH,
} from '../../../lib/readiness';
import { isSafeReturnTo, readReturnToParam } from '../../../lib/returnTo';
import { signIn as signInUser } from '../../../stores';
import { MutedText } from '../../common/MutedText';
import { GlobalPlaceholder } from '../../common/Placeholders';

const { confirm } = Modal;

function confirmRetry() {
  return confirm({
    okText: 'Retry',
    title: 'SignIn Failed',
    icon: <ExclamationCircleOutlined />,
    content: (
      <MutedText>
        SignIn failed. <br />
        Please try again.
      </MutedText>
    ),
    onCancel() {
      window.location.href = 'https://app.pixelmeapp.com/';
    },
    onOk() {
      window.location.href = '/sign-out';
    },
  });
}

/**
 * `/sign-in` - unauthenticated entry that starts Cognito authorize.
 *
 * The authorization-code exchange happens in the v2 app at `/v2/sign-in`.
 * After tokens are stored, v2 full-page-returns here for legacy destinations.
 * This page still resolves a Cognito identity to a PixelMe user when someone
 * is already signed in and visits `/sign-in` directly.
 */
export function CognitoSignIn() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { isLoading, isAuthenticated, error, user, signIn } = useCognitoAuth();

  // React 18 StrictMode double-invokes effects in development, and both the
  // authorize redirect and the /user load are side effects that must not run
  // twice.
  const startedRef = useRef(false);
  const [loadingText, setLoadingText] = useState('Loading account');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // A leftover `/sign-in` callback registration would land the code here.
    // Forward it to the shared v2 callback instead of starting a new
    // authorize request, which would overwrite the PKCE verifier.
    if (params.has('code') || params.has('error')) {
      window.location.replace(`/v2/sign-in${window.location.search}${window.location.hash}`);

      return;
    }

    if (isLoading || startedRef.current) {
      return;
    }

    if (error) {
      startedRef.current = true;
      console.error(error);
      confirmRetry();

      return;
    }

    if (!isAuthenticated) {
      startedRef.current = true;

      const invitationCode = localStorage.getItem('PIX_INVITATION_CODE');

      signIn({
        // Cognito Managed Login serves both registration and sign-in, so the
        // mode is recorded as application context rather than selecting an
        // endpoint. It decides what happens after the callback, not before.
        mode: invitationCode ? AuthMode.SignUp : AuthMode.Login,
        skipPermissionsFlow: !!invitationCode,
        // `?start_over` used to force Auth0's signup screen via `screen_hint`.
        // There is no equivalent under the `pixelme_spa` client - and
        // deliberately so, since self-service signup on this client is
        // blocked. Forcing a fresh credential prompt is the honest
        // counterpart: it stops the hosted SSO cookie from silently
        // resurrecting the session the user is trying to get out of.
        forceLogin: params.has('start_over'),
        returnTo: readReturnToParam(),
      });

      return;
    }

    startedRef.current = true;

    // `returnTo` rides in OIDC `state`, so it inherits the library's state
    // integrity check. Re-validated as same-origin regardless: a value that
    // survived a round trip is still untrusted input here.
    const stateReturnTo = user && user.state && user.state.returnTo;
    const returnTo = isSafeReturnTo(stateReturnTo) ? stateReturnTo : readReturnToParam();

    // The readiness gate provisions the local user and, when Hub onboarding
    // is not finished, sends the user there with `returnTo` so Hub brings
    // them back to where they were going. `/user` alone answers 401 for a
    // user who is not provisioned yet. See lib/readiness.js.
    ensureUserReady({ localStorage, axios, returnTo: returnTo || '/' })
      .then((result) => {
        if (result === ReadinessResult.RedirectingToHub) {
          setLoadingText(HUB_REDIRECT_LOADING_TEXT);
        }

        if (isRedirectingResult(result)) {
          return null;
        }

        return getSignedUser(localStorage, axios).then((signedUser) => {
          dispatch(signInUser(signedUser));
          history.push(returnTo || '/');
        });
      })
      .catch((err) => {
        // `/user` answers 404 for a user who exists but has no workspace yet
        // (see CurrentUser in servers/common/auth). The readiness gate
        // normally sends them to /v2/onboard first, off `is_onboarded`; this
        // is the fallback if the two ever disagree.
        if (err && err.response && err.response.status === 404) {
          window.location.href = V2_ONBOARD_PATH;

          return;
        }

        console.error(err);
        confirmRetry();
      });
  }, [isLoading, isAuthenticated, error, user, signIn, dispatch, history]);

  return <GlobalPlaceholder loadingText={loadingText} />;
}
